import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Avatar from '@/components/Avatar';
import Heatmap from '@/components/Heatmap';
import { Back as BackIcon, Search as SearchIcon } from '@/components/icons';
import { useOrg, ALL_ORGS } from '@/context/OrgContext';
import { useData } from '@/lib/useData';
import { formatRelative, safeId } from '@/lib/format';
import type { Person, ProfileData } from '@/lib/types';

const INACTIVE_DAYS = 30;

export default function Profiles() {
  const data = useData();
  const { org, label } = useOrg();
  const { id } = useParams();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  const people = useMemo(() => {
    const filtered = data.people.filter((p) => {
      if (org !== ALL_ORGS && (p.org_commits_90d?.[org] ?? 0) === 0) return false;
      if (!query) return true;
      const q = query.toLowerCase();
      return p.display_name.toLowerCase().includes(q)
        || (p.primary_login ?? '').toLowerCase().includes(q)
        || (p.email ?? '').toLowerCase().includes(q);
    });

    // Most-active first. Hard rule: active people (commit within
    // INACTIVE_DAYS) always come before inactive ones, regardless of
    // raw totals. Within each bucket, sort by org-specific 90-day commits
    // (or global total when no org filter), then by recency, then name.
    const score = (p: Person) =>
      org === ALL_ORGS
        ? (p.total_commits_90d ?? 0)
        : (p.org_commits_90d?.[org] ?? 0);

    return filtered.slice().sort((a, b) => {
      const ai = isInactive(a.last_active_at) ? 1 : 0;
      const bi = isInactive(b.last_active_at) ? 1 : 0;
      if (ai !== bi) return ai - bi;

      const diff = score(b) - score(a);
      if (diff !== 0) return diff;

      const la = a.last_active_at ?? '';
      const lb = b.last_active_at ?? '';
      if (la !== lb) return lb.localeCompare(la);
      return a.display_name.localeCompare(b.display_name);
    });
  }, [data.people, org, query]);

  const selectedId = id ?? (people[0] ? safeId(people[0].canonical_id) : null);
  const selected = useMemo(
    () => people.find((p) => safeId(p.canonical_id) === selectedId) ?? null,
    [people, selectedId],
  );

  // Only auto-select on desktop — on mobile we want the list first,
  // then the user taps a person to drill into the detail view.
  useEffect(() => {
    if (id || !people[0]) return;
    const isDesktop = typeof window !== 'undefined'
      && window.matchMedia('(min-width: 1024px)').matches;
    if (isDesktop) {
      navigate(`/profiles/${safeId(people[0].canonical_id)}`, { replace: true });
    }
  }, [id, people, navigate]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6">
      <aside className={[
        'lg:max-h-[calc(100vh-5rem)] lg:flex lg:flex-col lg:sticky lg:top-16',
        // On mobile, hide the list when a person is selected so the detail
        // takes the whole screen; on desktop always show both panes.
        id ? 'hidden lg:flex' : 'flex flex-col',
      ].join(' ')}>
        <div className="mb-3">
          <div className="flex items-baseline justify-between">
            <p className="text-[11px] uppercase tracking-wider font-semibold text-fg-subtle">{label(org)}</p>
            <span className="text-[11px] text-fg-subtle num">{people.length}</span>
          </div>
        </div>
        <div className="relative mb-2">
          <span className="absolute inset-y-0 left-2.5 flex items-center text-fg-subtle">
            <SearchIcon />
          </span>
          <input
            type="search"
            placeholder="Search people…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="input pl-8"
          />
        </div>
        <ul className="flex-1 overflow-y-auto space-y-0.5 -mx-1 px-1">
          {people.map((p) => (
            <PersonRow key={p.canonical_id} person={p} org={org} active={selectedId === safeId(p.canonical_id)} />
          ))}
        </ul>
      </aside>

      <section className={[
        'min-w-0',
        // On mobile, only show the detail pane when someone is selected.
        id ? 'block' : 'hidden lg:block',
      ].join(' ')}>
        {selected ? (
          <>
            <Link
              to="/profiles"
              className="lg:hidden inline-flex items-center gap-1.5 text-[12px] text-fg-muted hover:text-fg mb-3"
            >
              <BackIcon /> Back to people
            </Link>
            <ProfileDetail canonicalId={selected.canonical_id} />
          </>
        ) : (
          <div className="card p-10 text-center text-[13px] text-fg-subtle">Nobody matches.</div>
        )}
      </section>
    </div>
  );
}

function PersonRow({ person, org, active }: { person: Person; org: string; active: boolean }) {
  const count = org === ALL_ORGS ? (person.total_commits_90d ?? 0) : (person.org_commits_90d?.[org] ?? 0);
  const inactive = isInactive(person.last_active_at);
  return (
    <li>
      <Link to={`/profiles/${safeId(person.canonical_id)}`}
        className={[
          'group grid grid-cols-[auto_1fr_auto] items-center gap-2.5 px-2 py-1.5 rounded-md transition-colors',
          active ? 'bg-surface-hover' : 'hover:bg-surface-hover',
        ].join(' ')}>
        <Avatar name={person.display_name} login={person.primary_login} size={24} />
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 min-w-0">
            <span className={`text-[13px] font-medium truncate ${inactive ? 'text-fg-muted' : 'text-fg'}`}>{person.display_name}</span>
            {inactive && <span className="chip shrink-0 text-[10px]"><span className="chip-dot bg-fg-subtle" />inactive</span>}
          </div>
          {(person.email || (person.primary_login && person.primary_login.toLowerCase() !== person.display_name.toLowerCase())) && (
            <div className="text-[11px] text-fg-subtle truncate">{person.email ?? `@${person.primary_login}`}</div>
          )}
        </div>
        <span className="text-[11px] font-semibold num text-fg-subtle">{count}</span>
      </Link>
    </li>
  );
}

function isInactive(lastActive?: string | null): boolean {
  if (!lastActive) return true;
  const delta = Date.now() - new Date(lastActive).getTime();
  return delta > INACTIVE_DAYS * 86400 * 1000;
}

// --- profile detail loader -------------------------------------------------

function ProfileDetail({ canonicalId }: { canonicalId: string }) {
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancel = false;
    setProfile(null);
    setError(null);
    const safe = safeId(canonicalId);
    const load = async () => {
      try {
        const mod = await PROFILE_LOADERS[safe]?.();
        if (cancel) return;
        if (mod) setProfile(mod.default as ProfileData);
        else setError('No profile data.');
      } catch (e: unknown) {
        if (!cancel) setError(e instanceof Error ? e.message : 'Failed to load profile.');
      }
    };
    load();
    return () => { cancel = true; };
  }, [canonicalId]);

  if (error) return <div className="card p-8 text-[13px] text-fg-subtle">{error}</div>;
  if (!profile) return <div className="card p-8 text-[13px] text-fg-subtle">Loading…</div>;

  const p = profile.person;
  const secondary = p.email
    ?? (p.primary_login && p.primary_login.toLowerCase() !== p.display_name.toLowerCase() ? `@${p.primary_login}` : null);
  const inactive = isInactive(p.last_commit_at);

  const stats = [
    { label: '7d commits',    value: p.commits_7d },
    { label: '30d commits',   value: p.commits_30d },
    { label: '90d commits',   value: p.commits_90d },
    { label: 'active 30d',    value: p.active_days_30d },
    { label: 'repos 30d',     value: p.repos_touched_30d },
    { label: 'current streak', value: p.current_streak },
    { label: 'longest streak', value: p.longest_streak },
    { label: 'PRs merged 30d', value: p.prs_merged_30d },
    { label: 'reviews 30d',    value: p.reviews_given_30d },
    { label: 'AI 30d',         value: p.ai_assisted_30d },
    { label: 'BHAI 30d',       value: p.bhai_prs_merged_30d },
    { label: 'total commits',  value: p.total_commits },
  ];

  return (
    <div className="space-y-5">
      <header className="card p-5 flex items-start gap-4">
        <Avatar name={p.display_name} login={p.primary_login} size={56} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-[18px] font-semibold tracking-tight-plus text-fg truncate">{p.display_name}</h1>
            {inactive && <span className="chip"><span className="chip-dot bg-fg-subtle" />inactive</span>}
          </div>
          {secondary && <div className="text-[12px] text-fg-muted">{secondary}</div>}
          <div className="mt-2 flex items-center gap-2.5 text-[11px] text-fg-subtle">
            {p.role_hint && <span className="chip uppercase tracking-wider">{p.role_hint}</span>}
            <span>Last commit {formatRelative(p.last_commit_at)}</span>
          </div>
        </div>
      </header>

      {profile.badges.length > 0 && (
        <section className="flex flex-wrap gap-1.5">
          {profile.badges.map((b) => (
            <span key={b.key} className="chip" title={b.description}>
              <span className="chip-dot bg-accent" />{b.label}
            </span>
          ))}
        </section>
      )}

      <section>
        <div className="text-[11px] uppercase tracking-wider font-semibold text-fg-subtle mb-2">Activity | 365 days</div>
        <div className="card p-3">
          <Heatmap weeks={profile.heatmap} />
        </div>
      </section>

      <section className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {stats.map((s) => (
          <div key={s.label} className="card p-3">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-fg-subtle">{s.label}</div>
            <div className="text-[17px] font-semibold num mt-0.5">{s.value}</div>
          </div>
        ))}
      </section>

      {profile.repo_breakdown.length > 0 && (
        <section>
          <div className="text-[11px] uppercase tracking-wider font-semibold text-fg-subtle mb-2">Top repos | 90d</div>
          <div className="card divide-y">
            {profile.repo_breakdown.map((r) => (
              <div key={`${r.org}/${r.name}`} className="px-3 py-2 flex items-center justify-between text-[13px]">
                <span className="mono text-[12px] text-fg-muted truncate">{r.org}/{r.name}</span>
                <span className="font-semibold num">{r.n}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {profile.recent_commits.length > 0 && (
        <section>
          <div className="text-[11px] uppercase tracking-wider font-semibold text-fg-subtle mb-2">Recent commits</div>
          <div className="card divide-y">
            {profile.recent_commits.map((c) => (
              <div key={c.sha} className="px-3 py-2.5 text-[13px]">
                <div className="flex items-center gap-2 flex-wrap text-[11px] text-fg-subtle">
                  <span className="mono">{c.sha.slice(0, 7)}</span>
                  <span className="mono truncate">{c.org}/{c.repo_name}</span>
                  {c.is_ai_assisted === 1 && <span className="chip"><span className="chip-dot bg-accent" />AI</span>}
                  {c.is_bhai_assisted === 1 && <span className="chip"><span className="chip-dot bg-fivec" />BHAI</span>}
                  <span className="ml-auto">{formatRelative(c.committed_at)}</span>
                </div>
                <p className="mt-0.5 text-fg truncate">{c.message_first_line}</p>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

// Vite-native dynamic imports for per-person profile JSON.
const RAW_LOADERS = import.meta.glob('../data/profiles/*.json') as Record<
  string,
  () => Promise<{ default: ProfileData }>
>;
const PROFILE_LOADERS: Record<string, () => Promise<{ default: ProfileData }>> = {};
for (const [path, loader] of Object.entries(RAW_LOADERS)) {
  const match = /\/([^/]+)\.json$/.exec(path);
  if (match?.[1]) PROFILE_LOADERS[match[1]] = loader;
}

